/**
 * OpenSpec 021 — conflictos de identidad al editar una ficha de contacto.
 *
 * El e-mail y el WhatsApp identifican a la persona: el e-mail tiene `UNIQUE (email, website_id)`
 * en `customer_entity` y el WhatsApp es la clave del login por WhatsApp (EAV 369). Escribir en una
 * ficha el dato de otra identidad no es un typo cualquiera — casi siempre significa que son la
 * misma persona con dos cuentas, y eso es exactamente lo que resuelve la unificación.
 */
import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

export interface IdentityConflictContact {
  contactId: number;
  storeCode: string;
  archived: boolean;
}

export interface IdentityConflict {
  field: "email" | "whatsapp";
  /** El valor normalizado del lado del server. */
  value: string;
  userId: number;
  name: string;
  email: string;
  whatsapp?: string | null;
  /** El e-mail del otro es un placeholder `<numero>@wppxcons.com` del alta por WhatsApp. */
  isSyntheticEmail: boolean;
  contacts: IdentityConflictContact[];
  /**
   * Con `contactId` en la consulta (siempre, desde la ficha) es, como máximo, el store de esa
   * ficha: el operador está parado en un store y los demás no le importan.
   */
  sharedStores: string[];
  /** El store al que quedó scopeada la respuesta: el de la ficha que se está editando. */
  scopeStoreCode: string | null;
  /** `false` ⇒ no se puede unificar acá; el motivo está en `blockedReason`. */
  canMerge: boolean;
  /** Por qué no se puede unificar. Cada motivo manda a hacer algo distinto. */
  blockedReason:
    | "not_in_store"
    | "source_archived"
    | "target_archived"
    | "no_shared_store"
    | null;
  /** Ficha del OTRO contacto **en el store de la ficha** — el **origen** de la unificación. */
  sourceContactId: number | null;
  /** Ficha del contacto que se está editando en ese store — el **destino**. */
  targetContactId: number | null;
}

export interface IdentityConflicts {
  email: IdentityConflict | null;
  whatsapp: IdentityConflict | null;
}

const EMPTY: IdentityConflicts = { email: null, whatsapp: null };

/**
 * `contactId` es la ficha desde la que se está editando, y **scopea la respuesta a su store**. Una
 * identidad puede ser contacto en varios stores: sin esto el backend elegía el par origen/destino
 * en cualquier store compartido, así que editando la ficha de un store el drawer podía abrir el par
 * de otro — y al terminar la unificación navegaba a esa otra ficha.
 */
export async function fetchIdentityConflicts(
  userId: number,
  input: { email?: string; whatsapp?: string; contactId?: string | number },
): Promise<IdentityConflicts> {
  if (!userId || (!input.email && !input.whatsapp)) return EMPTY;
  const { data } = await axiosInstance.get(
    `${API_BASE_URL}/users/${userId}/identity-conflicts`,
    {
      params: {
        ...(input.email ? { email: input.email } : {}),
        ...(input.whatsapp ? { whatsapp: input.whatsapp } : {}),
        ...(input.contactId != null ? { contactId: input.contactId } : {}),
      },
    },
  );
  return { email: data?.email ?? null, whatsapp: data?.whatsapp ?? null };
}

/**
 * Lee el 409 `IDENTITY_CONFLICT` del `PATCH /users/:userId`.
 *
 * El chequeo previo puede quedar viejo (otro operador se queda con el dato entre el blur y el
 * guardado), así que el error del backend se trata igual que el conflicto detectado a tiempo.
 */
export function readIdentityConflictError(error: any): IdentityConflicts | null {
  const payload = error?.response?.data;
  if (payload?.code !== "IDENTITY_CONFLICT") return null;
  const conflicts = payload?.conflicts;
  if (!conflicts) return null;
  return { email: conflicts.email ?? null, whatsapp: conflicts.whatsapp ?? null };
}
